<script setup lang="ts">
  import { computed, onBeforeMount, ref } from 'vue';

  import type { Partner, Tag } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
  } from '@ui/components/command';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import TagPreview from '@/components/tags/TagPreview.vue';

  interface Props {
    partner: Partner;
  }

  const props = defineProps<Props>();

  const open = ref(false);
  const isLoadingTags = ref(true);
  const isUpdating = ref(false);

  const tags = ref<Tag[]>([]);

  const selectedTags = computed(() =>
    tags.value.filter((tag) => {
      return props.partner.rawTags.find((t) => t.id === tag.id);
    }),
  );
  const unselectedTags = computed(() =>
    tags.value.filter((tag) => !props.partner.rawTags.find((t) => t.id === tag.id)),
  );

  onBeforeMount(() => {
    stuut.tags
      .list()
      .then((t) => (tags.value = t))
      .catch((error) => toast.error(error.clientMessage))
      .finally(() => (isLoadingTags.value = false));
  });

  const applyTag = (tag: Tag) => {
    if (isUpdating.value) return;
    isUpdating.value = true;
    stuut.partners
      .applyTag(props.partner, tag)
      .catch((err) => toast.error(`Failed to apply tag with error: ${err}`))
      .finally(() => (isUpdating.value = false));
  };

  const removeTag = (tag: Tag) => {
    if (isUpdating.value) return;
    isUpdating.value = true;

    stuut.partners
      .removeTag(props.partner, tag)
      .catch((err) => toast.error(`Failed to remove tag with error: ${err}`))
      .finally(() => (isUpdating.value = false));
  };
</script>

<template>
  <div class="flex gap-1 items-center">
    <span v-if="partner.rawTags.length === 0" class="text-xs text-muted-foreground">
      (No tags)
    </span>
    <TagPreview v-for="tag in partner.rawTags" :key="tag.id" :name="tag.name" />
    <Popover v-model:open="open">
      <PopoverTrigger as-child>
        <Button
          variant="ghost"
          size="icon-sm"
          role="combobox"
          :aria-expanded="open"
          :loading="isUpdating || isLoadingTags"
        >
          <i class="fa far fa-pencil" />
        </Button>
      </PopoverTrigger>

      <PopoverContent class="w-fit p-0">
        <Command
          :filter-function="
            (list, term) => {
              term = term.toLowerCase();
              return (list as Record<string, any>[]).filter((i) =>
                i.name?.toLowerCase()?.includes(term),
              );
            }
          "
        >
          <CommandList>
            <div class="relative">
              <CommandInput placeholder="Search tags..." />
            </div>
            <CommandEmpty>No tags found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                v-for="tag in selectedTags"
                :key="tag.id"
                :value="tag"
                @select="
                  () => {
                    removeTag(tag);
                    open = false;
                  }
                "
              >
                <TagPreview :name="tag.name" />
              </CommandItem>
              <CommandItem
                v-for="tag in unselectedTags"
                :key="tag.id"
                :value="tag"
                @select="
                  () => {
                    applyTag(tag);
                    open = false;
                  }
                "
              >
                <TagPreview :name="tag.name" />
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  </div>
</template>

<style scoped></style>
